import { Box, Dialog, FormLabel } from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { FC, forwardRef, useMemo } from 'react';
import { Form } from 'react-final-form';
import { TrackerDateTime } from '../shared/TrackerDateTime';
import { TRACKERS_SUB_TYPE } from '../../../utils/constants';
import { TimeOfTheDayField } from '../sleep/TimeOfTheDayField';
import { BreastField } from '../shared/BreastField';
import { useTranslation } from 'react-i18next';
import { CryField } from '../cry/CryField';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type ResultType = {
  startTime: Date;
  endTime: Date;
  additionalInfo: string;
  duration?: number;
};

interface TrackingEditDialogProps {
  trackerType: string;
  isOpen: boolean;
  onClose?: () => void;
  onSave?: (data: ResultType) => void;
  startTime?: Date;
  quantity?: number;
  additionalInfo?: string;
  isEdit?: boolean;
}

export const TrackingEditDialog: FC<TrackingEditDialogProps> = ({
  onSave,
  onClose,
  startTime: _startTime,
  quantity,
  additionalInfo: _additionalInfo,
  isEdit,
  trackerType,
}) => {
  const { t } = useTranslation();
  const handleClose = () => {
    onClose && onClose();
  };

  const handleSubmit = (values: ResultType) => {
    onSave &&
      onSave({
        ...values,
        duration: moment(values.endTime).diff(
          moment(values.startTime),
          'seconds'
        ),
      });
    handleClose();
  };

  const initialValues = useMemo(() => {
    const startTime = moment(_startTime);
    const endTime = quantity
      ? moment(startTime).add(quantity, 'seconds')
      : moment(startTime).add(1, 'minutes');
    return {
      startTime: moment(startTime.format('YYYY-MM-DD HH:mm')).toDate(),
      endTime: moment(endTime.format('YYYY-MM-DD HH:mm')).toDate(),
      additionalInfo: _additionalInfo,
    } as ResultType;
  }, [_startTime, quantity, _additionalInfo]);

  let RadioButtonField: any;

  switch (trackerType) {
    case TRACKERS_SUB_TYPE.SLEEP:
      RadioButtonField = TimeOfTheDayField;
      break;
    case TRACKERS_SUB_TYPE.BREAST_FEEDING:
      RadioButtonField = BreastField;
      break;
    case TRACKERS_SUB_TYPE.CRY:
      RadioButtonField = CryField;
      break;
    default:
      RadioButtonField = null;
      break;
  }

  const action = isEdit ? 'edit' : 'add';
  const title =
    trackerType === TRACKERS_SUB_TYPE.SLEEP
      ? t('trackers:trackingEditDialog:title.simpleAdd')
      : t(`trackers:trackingEditDialog:title.${action}`, {
          trackerName: t(
            `trackers:trackingScreen:trackers.${trackerType}`
          ).toLowerCase(),
        });

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validate={(values) => {
        const errors: Record<string, string> = {};
        if (!values.startTime) {
          errors.startTime = 'Required';
        }
        if (!values.endTime) {
          errors.endTime = 'Required';
        }
        if (!values.additionalInfo) {
          errors.additionalInfo = 'Required';
        }
        if (
          values.startTime &&
          values.endTime &&
          moment(values.startTime).isAfter(moment(values.endTime))
        ) {
          errors.startTime = t('trackers:trackingEditDialog:errors.startTime');
        }
        if (
          values.startTime &&
          values.endTime &&
          moment(values.endTime).diff(moment(values.startTime), 'minutes') < 1
        ) {
          errors.endTime = t('trackers:trackingEditDialog:errors.endTime');
        }
        return errors;
      }}
    >
      {({ handleSubmit }) => (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Dialog
            open={true}
            TransitionComponent={Transition}
            onClose={onClose}
          >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent id="note-field-box">
              <RadioButtonField name="additionalInfo" />
              <Box>
                <FormLabel>
                  {t('trackers:trackingEditDialog:startLabel')}
                </FormLabel>
                <TrackerDateTime name="startTime" />
                <FormLabel>
                  {t('trackers:trackingEditDialog:endLabel')}
                </FormLabel>
                <TrackerDateTime name="endTime" />
              </Box>
            </DialogContent>
            <DialogActions id="note-button-box">
              <Button
                variant="outlined"
                className="delete-button"
                onClick={handleClose}
              >
                {t('trackers:trackingEditDialog:buttons.close')}
              </Button>
              <Button variant="contained" onClick={handleSubmit}>
                {t(`trackers:trackingEditDialog:buttons.${action}`)}
              </Button>
            </DialogActions>
          </Dialog>
        </LocalizationProvider>
      )}
    </Form>
  );
};
